/**
 * User-ended call tombstones (2026-08 post-hangup reconnect-loop fix).
 *
 * When a call reaches a terminal phase ON THIS DEVICE, we record its callId
 * here. `rehydrateActiveCallFromBackend` / auto-join / switch-to-this-device
 * must consult the tombstones BEFORE acting on a backend SESSION row: a stale
 * row (peer's END never landed, sweeper hasn't run) must never resurrect a
 * call the user already ended — that was the zombie reconnect loop.
 *
 * Persisted in localStorage so a WebView reload (the exact moment rehydrate
 * runs) cannot forget the hangup. Storage failures degrade to the in-memory
 * map — same session-level guarantees, no crash.
 */

import type { VoiceCallEndReason } from '../types/voice-call'

const STORAGE_KEY = 'ascend-ended-call-tombstones'
/** Longer than any backend session-liveness window (6h orphan cutoff). */
const TOMBSTONE_TTL_MS = 6 * 60 * 60 * 1000
const MAX_ENTRIES = 30

type Tombstone = { endedAtMs: number; reason: string }

const memory = new Map<string, Tombstone>()

function readStore(): Record<string, Tombstone> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    const out: Record<string, Tombstone> = {}
    for (const [callId, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue
      const endedAtMs = (value as { endedAtMs?: unknown }).endedAtMs
      const reason = (value as { reason?: unknown }).reason
      if (typeof endedAtMs !== 'number' || !Number.isFinite(endedAtMs)) continue
      out[callId] = { endedAtMs, reason: typeof reason === 'string' ? reason : 'ended' }
    }
    return out
  } catch {
    return {}
  }
}

function writeStore(entries: Record<string, Tombstone>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch {
    /* storage unavailable — memory map still covers this session */
  }
}

function pruned(entries: Record<string, Tombstone>, nowMs: number): Record<string, Tombstone> {
  const kept = Object.entries(entries)
    .filter(([, t]) => nowMs - t.endedAtMs < TOMBSTONE_TTL_MS)
    .sort(([, a], [, b]) => b.endedAtMs - a.endedAtMs)
    .slice(0, MAX_ENTRIES)
  return Object.fromEntries(kept)
}

/** Record that this call ended (terminal phase reached) on this device. */
export function recordCallEndedTombstone(
  callId: string,
  reason: VoiceCallEndReason | string = 'ended',
  nowMs: number = Date.now(),
): void {
  if (!callId) return
  const tombstone: Tombstone = { endedAtMs: nowMs, reason: String(reason) }
  memory.set(callId, tombstone)
  writeStore(pruned({ ...readStore(), [callId]: tombstone }, nowMs))
}

/** True when the user/device already saw this call end — never resurrect it. */
export function isCallTombstoned(callId: string, nowMs: number = Date.now()): boolean {
  if (!callId) return false
  const inMemory = memory.get(callId)
  if (inMemory && nowMs - inMemory.endedAtMs < TOMBSTONE_TTL_MS) return true
  const stored = readStore()[callId]
  return Boolean(stored && nowMs - stored.endedAtMs < TOMBSTONE_TTL_MS)
}

/** The recorded end reason for a tombstoned call ('ended' when unknown). */
export function tombstonedCallReason(callId: string): string | null {
  const t = memory.get(callId) ?? readStore()[callId]
  return t ? t.reason : null
}

/** Test hook. */
export function clearCallEndedTombstones(): void {
  memory.clear()
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}
